import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { DashboardLayout } from "../../layouts/DashboardLayout"
import { createCourseApi, uploadCourseCoverApi } from "../../features/courses/api/course.api"
import { listMentorsApi, UserProfileItem } from "../../features/users/api/profile.api"
import { CategoryItem, listCategoriesApi } from "../../features/categories/api/category.api"
import { listCoursePriceTiersApi, PriceTierOption } from "../../features/payments/api/payments.api"
import { MultiSelect } from "../../shared/ui/MultiSelect"
import { SelectField } from "../../shared/ui/SelectField"
import { SectionsEditor, CourseSection } from "../../features/courses/ui/SectionsEditor"
import {
  PhotoIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
} from "@heroicons/react/16/solid"

/* ── types ────────────────────────────────────────────── */

type CourseLevel = "beginner" | "intermediate" | "advanced"
type PriceType = "free" | "paid" | "mixed"
type Section = CourseSection

/* ── small shared components ─────────────────────────── */

const FieldLabel = ({ children, required }: { children: React.ReactNode; required?: boolean }) => (
  <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">
    {children}
    {required && <span className="text-red-400 ml-1">*</span>}
  </label>
)

const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white"

const Card = ({ title, children }: { title?: string; children: React.ReactNode }) => (
  <div className="bg-white rounded-xl border">
    {title && (
      <div className="px-5 py-3.5 border-b bg-gray-50/60 rounded-t-xl">
        <p className="text-sm font-semibold text-gray-700">{title}</p>
      </div>
    )}
    <div className="p-5 space-y-4">{children}</div>
  </div>
)

/* ── page ─────────────────────────────────────────────── */

export const CreateCoursePage = () => {
  const navigate = useNavigate()

  /* main */
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [level, setLevel] = useState<CourseLevel>("beginner")
  const [duration, setDuration] = useState(0)
  const [language, setLanguage] = useState("ru")
  const [requiresExperience, setRequiresExperience] = useState(false)

  /* cover */
  const [coverImageId, setCoverImageId] = useState("")

  /* price */
  const [priceType, setPriceType] = useState<PriceType>("free")
  const [priceAmount, setPriceAmount] = useState(0)
  const [priceTiers, setPriceTiers] = useState<PriceTierOption[]>([])

  /* relations */
  const [authorId, setAuthorId] = useState("")
  const [mentorIds, setMentorIds] = useState<string[]>([])
  const [categoryIds, setCategoryIds] = useState<string[]>([])

  /* sections */
  const [sections, setSections] = useState<Section[]>([])

  /* data */
  const [authors, setAuthors] = useState<UserProfileItem[]>([])
  const [mentors, setMentors] = useState<UserProfileItem[]>([])
  const [categories, setCategories] = useState<CategoryItem[]>([])

  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    Promise.all([listMentorsApi(), listCategoriesApi(), listCoursePriceTiersApi()]).then(
      ([{ data: mentorsRes }, { data: categoriesRes }, { data: tiersRes }]) => {
        setMentors(mentorsRes.items)
        setAuthors(mentorsRes.items)
        setCategories(categoriesRes.items)
        setPriceTiers(tiersRes.items)
      }
    )
  }, [])

  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      const { data } = await createCourseApi({
        title,
        description,
        cover_image_id: coverImageId || null,
        level,
        duration_minutes: duration,
        language,
        requires_experience: requiresExperience,
        price: { type: priceType, amount: priceType === "free" ? 0 : priceAmount },
        author_id: authorId || null,
        mentor_ids: mentorIds,
        category_ids: categoryIds,
        sections,
      })
      navigate(`/courses/${data.id}`)
    } catch {
      alert("Не удалось создать курс")
    } finally {
      setSubmitting(false)
    }
  }

  const canSubmit = !!title && !!coverImageId && !submitting && (priceType === "free" || priceAmount > 0)

  return (
    <DashboardLayout title="Создание курса">
      <div className="max-w-5xl mx-auto space-y-5">

        {/* breadcrumb */}
        <button
          onClick={() => navigate("/courses")}
          className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-blue-500 transition-colors"
        >
          <ChevronLeftIcon className="w-3.5 h-3.5" />
          Назад к курсам
        </button>

        <div className="grid grid-cols-3 gap-5">

          {/* ── LEFT ── */}
          <div className="col-span-2 space-y-5">

            {/* Basic info */}
            <Card title="Основная информация">
              <div>
                <FieldLabel required>Название</FieldLabel>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="например, Продвинутый курс маркетинга"
                  className={inputCls}
                />
              </div>

              <div>
                <FieldLabel>Описание</FieldLabel>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  placeholder="Чему научатся студенты на этом курсе?"
                  className={`${inputCls} resize-none`}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <FieldLabel>Уровень</FieldLabel>
                  <div className="flex gap-2">
                    {(["beginner", "intermediate", "advanced"] as CourseLevel[]).map((l) => (
                      <button
                        key={l}
                        onClick={() => setLevel(l)}
                        className={`flex-1 py-2 rounded-lg text-xs font-medium border capitalize transition-all ${
                          level === l
                            ? "border-blue-500 bg-blue-50 text-blue-700 shadow-sm"
                            : "border-gray-200 text-gray-500 hover:border-gray-300"
                        }`}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <FieldLabel>Язык</FieldLabel>
                  <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    className={inputCls}
                  >
                    <option value="ru">Русский</option>
                    <option value="en">Английский</option>
                    <option value="kz">Казахский</option>
                    <option value="kg">Кыргызский</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <FieldLabel>Длительность (минут)</FieldLabel>
                  <input
                    type="number"
                    min={0}
                    value={duration === 0 ? "" : duration}
                    onChange={(e) => setDuration(e.target.value === "" ? 0 : Number(e.target.value))}
                    placeholder="например, 120"
                    className={inputCls}
                  />
                </div>

                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2.5 cursor-pointer select-none">
                    <div
                      onClick={() => setRequiresExperience((v) => !v)}
                      className={`w-10 h-5.5 rounded-full transition-colors relative cursor-pointer flex-shrink-0 ${
                        requiresExperience ? "bg-blue-600" : "bg-gray-200"
                      }`}
                      style={{ height: "22px" }}
                    >
                      <div
                        className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${
                          requiresExperience ? "left-5" : "left-0.5"
                        }`}
                      />
                    </div>
                    <span className="text-sm text-gray-600">Требуется опыт</span>
                  </label>
                </div>
              </div>
            </Card>

            {/* Price */}
            <Card title="Стоимость">
              <div>
                <FieldLabel>Тип цены</FieldLabel>
                <div className="grid grid-cols-3 gap-2">
                  {(["free", "paid", "mixed"] as PriceType[]).map((t) => {
                    const meta = {
                      free: { label: "Бесплатно", desc: "Оплата не требуется" },
                      paid: { label: "Платно", desc: "Единоразовый платёж" },
                      mixed: { label: "Смешанный", desc: "Часть уроков бесплатна" },
                    }[t]
                    return (
                      <button
                        key={t}
                        onClick={() => setPriceType(t)}
                        className={`p-3 rounded-xl border text-left transition-all ${
                          priceType === t
                            ? "border-blue-500 bg-blue-50 shadow-sm"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <p className={`text-sm font-semibold ${priceType === t ? "text-blue-700" : "text-gray-700"}`}>
                          {meta.label}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">{meta.desc}</p>
                      </button>
                    )
                  })}
                </div>
              </div>

              {priceType !== "free" && (
                <div>
                  <FieldLabel required>Цена (уровень IAP, $)</FieldLabel>
                  <SelectField
                    value={priceAmount ? String(priceAmount) : ""}
                    onChange={(v) => setPriceAmount(Number(v))}
                    placeholder="Выберите уровень цены"
                    options={priceTiers.map((t) => ({
                      value: String(t.tier),
                      label: `$${t.tier} (${(t.price_usd_cents / 100).toFixed(2)})`,
                    }))}
                  />
                  <p className="text-[11px] text-gray-400 mt-1">
                    Цена — фиксированный уровень из сетки Apple/Google IAP, свободный ввод отключён.
                    Управление уровнями — в «Платежи → IAP-уровни».
                  </p>
                </div>
              )}
            </Card>

            {/* Sections */}
            <Card title="Разделы курса">
              <SectionsEditor sections={sections} setSections={setSections} />
            </Card>
          </div>

          {/* ── RIGHT ── */}
          <div className="space-y-5">

            {/* Cover */}
            <Card title="Обложка">
              <ImageUploadBlock required onChange={setCoverImageId} />
            </Card>

            {/* Relations */}
            <Card title="Люди и категории">
              <div>
                <FieldLabel>Автор</FieldLabel>
                <select
                  value={authorId}
                  onChange={(e) => setAuthorId(e.target.value)}
                  className={inputCls}
                >
                  <option value="">Выберите автора</option>
                  {authors.map((a) => (
                    <option key={a.user_id} value={a.user_id}>
                      {a.full_name ?? a.user_id}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <FieldLabel>Менторы</FieldLabel>
                <MultiSelect
                  values={mentorIds}
                  onChange={setMentorIds}
                  options={mentors.map((m) => ({ value: m.user_id, label: m.full_name ?? m.user_id }))}
                  placeholder="Выберите менторов…"
                />
              </div>

              <div>
                <FieldLabel>Категории</FieldLabel>
                <MultiSelect
                  values={categoryIds}
                  onChange={setCategoryIds}
                  options={categories.map((c) => ({ value: c.id, label: c.title }))}
                  placeholder="Выберите категории…"
                />
              </div>
            </Card>

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
            >
              <CheckCircleIcon className="w-4 h-4" />
              {submitting ? "Создание…" : "Создать курс"}
            </button>

            {!coverImageId && (
              <p className="text-xs text-center text-gray-400">
                Обложка обязательна
              </p>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}

/* ── image upload ─────────────────────────────────────── */

const ImageUploadBlock = ({
  onChange,
  required = false,
}: {
  onChange: (id: string) => void
  required?: boolean
}) => {
  const [preview, setPreview] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [uploaded, setUploaded] = useState(false)

  const handleUpload = async (file: File) => {
    setPreview(URL.createObjectURL(file))
    setLoading(true)
    setUploaded(false)
    try {
      const { data } = await uploadCourseCoverApi(file)
      onChange(data.id)
      setUploaded(true)
    } catch {
      alert("Не удалось загрузить изображение")
      setPreview(null)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-2">
      <div
        className={`relative aspect-video rounded-xl overflow-hidden border-2 border-dashed transition-colors ${
          preview ? "border-transparent" : "border-gray-200 hover:border-blue-300 bg-gray-50"
        }`}
      >
        {preview ? (
          <img src={preview} className="absolute inset-0 w-full h-full object-cover" alt="cover" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-gray-400">
            <PhotoIcon className="w-8 h-8 text-gray-300" />
            <p className="text-xs font-medium">Нажмите, чтобы загрузить обложку</p>
            <p className="text-xs text-gray-300">Рекомендуется 16:9</p>
          </div>
        )}

        {loading && (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
            <div className="text-xs text-gray-500 animate-pulse">Загрузка…</div>
          </div>
        )}

        <input
          type="file"
          accept="image/*"
          className="absolute inset-0 opacity-0 cursor-pointer"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) handleUpload(file)
          }}
        />
      </div>

      {uploaded && (
        <p className="text-xs text-emerald-600 flex items-center gap-1">
          <CheckCircleIcon className="w-3.5 h-3.5" />
          Успешно загружено
        </p>
      )}
    </div>
  )
}
